import { supabase } from "@/integrations/supabase/client";
import { capture } from "@/lib/sentry/capture";
import type { ContainerSignature, SignedContainerInfo } from "./signatureModel";
import { deriveSignatureStatus } from "./signatureModel";

export interface SignatureInspection {
  document: { id: number; filename: string; size: number | null };
  state: "complete" | "pending" | "unsupported" | "failed";
  checkedAt: string | null;
  signatures: Array<{
    id: string;
    signerName: string;
    signerCountry: string;
    signedAt: string | null;
    timestampAt: string | null;
    signatureType: string | null;
    validity: ContainerSignature["validity"];
    warningCount: number;
  }>;
  errorCode: string | null;
  policy: "aes";
}

export function canInspectSignatures(filename: string): boolean {
  return /\.(pdf|asice|asics|asic|bdoc|ddoc|adoc|edoc|mdoc)$/i.test(filename);
}

export async function inspectSignatures(
  documentId: string,
  refresh = false
): Promise<SignatureInspection> {
  const response = await supabase.functions.invoke("dokobit-inspect", {
    body: { document_id: Number(documentId), refresh },
  });
  const data = response.data as SignatureInspection | null;
  if (response.error || !data) {
    // The SDK error can carry response bodies. Keep monitoring free of signer data.
    const failure = new Error("Signature inspection request failed");
    capture(failure, {
      tags: { integration: "dokobit", action: "inspect_signatures" },
      fingerprint: ["dokobit", "inspect-signatures", "request"],
      context: { document: { id: documentId } },
    });
    throw failure;
  }
  return data;
}

export function inspectedDocumentInfo(result: SignatureInspection): SignedContainerInfo {
  const signatures: ContainerSignature[] = result.signatures.map((signature) => ({
    ...signature,
    method: "",
  }));
  return {
    containerId: String(result.document.id),
    containerFilename: result.document.filename,
    containerSize: result.document.size,
    signatures,
    status:
      result.state === "complete" && signatures.length === 0
        ? "unsigned"
        : deriveSignatureStatus(signatures),
    inspectionComplete: result.state === "complete",
  };
}
